/**
 * Bill Validation — composing report emails, one bill or many.
 *
 * Shared by `SendBillReportDialog` (one bill, editable) and
 * `BulkSendReportsDialog` (many bills, one template), so a bulk-sent email says
 * exactly what a single send of the same bill would have said.
 */

import type { BvEmailTemplate, BvReportContext } from '../services/bvReportService';
import { fillTokens } from './tokenFill';
import { RAW_HTML_TOKENS } from './reportTokens';

/**
 * The covering email used when the org has saved no `bill_validation` email
 * template. Written in the same `{token}` vocabulary as a saved template, so
 * swapping one in later changes nothing about how it is filled.
 *
 * Add templates under Bill Validation → Email Templates.
 */
export const FALLBACK_EMAIL_SUBJECT = 'Bill Validation Report — {nmi} — {billing_period}';

export const FALLBACK_EMAIL_BODY = `<p>Hi {client_name},</p>
<p>Please find attached the bill validation report for your {billing_period} invoice for NMI {nmi}.</p>
<p>Expected invoice total: <strong>{expected_total}</strong><br>
Retailer invoice total: <strong>{invoiced_total}</strong><br>
Variance: <strong>{variance_dollars} ({variance_percent})</strong></p>
<p>{overcharge_summary}</p>
<p>If you have any questions about the figures, reply to this email and we'll walk you through them.</p>
<p>Kind regards,<br>Choice Energy</p>`;

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function composeReportEmail(
  template: BvEmailTemplate | null | undefined,
  tokens: Record<string, string>,
): { subject: string; bodyHtml: string } {
  const rawSubject = template?.subject || FALLBACK_EMAIL_SUBJECT;
  const rawBody = template?.body_html || FALLBACK_EMAIL_BODY;
  return {
    subject: fillTokens(rawSubject, tokens, { rawKeys: RAW_HTML_TOKENS }).html.trim(),
    bodyHtml: fillTokens(rawBody, tokens, { rawKeys: RAW_HTML_TOKENS }).html,
  };
}

export type BulkSendInput = {
  billId: string;
  context: BvReportContext | null;
  /** Why the context could not be loaded, when it could not. */
  error: string | null;
};

export type BulkSendRow = {
  billId: string;
  state: 'ready' | 'skipped';
  reason: string | null;
  recipients: string[];
  warnings: string[];
  context: BvReportContext | null;
};

/**
 * Which bills a bulk send will actually email. A bill is skipped — shown, not
 * silently dropped — when its figures could not be read or nobody on file has a
 * usable address. Calculation warnings do not skip: they were visible when the
 * report was reviewed, and the confirm table repeats them.
 */
export function planBulkSend(inputs: BulkSendInput[]): BulkSendRow[] {
  return inputs.map(({ billId, context, error }) => {
    if (!context) {
      return { billId, state: 'skipped', reason: error ?? 'Could not read this bill’s figures', recipients: [], warnings: [], context: null };
    }
    const recipients = [...new Set(
      context.suggestedRecipients.map((r) => r.email.trim().toLowerCase()).filter((e) => EMAIL_RE.test(e)),
    )];
    const warnings = [...context.meta.warnings, ...(context.meta.matchWarning ? [context.meta.matchWarning] : [])];
    if (recipients.length === 0) {
      return { billId, state: 'skipped', reason: 'No recipient on file for this client', recipients, warnings, context };
    }
    return { billId, state: 'ready', reason: null, recipients, warnings, context };
  });
}
